<script setup>
import { computed, ref, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentBotsAPI from 'dashboard/api/agentBots';
import StudioSection from './StudioSection.vue';
import {
  createConfigDraft,
  getPath,
  provideConfigDraft,
} from './configDraft';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
  fields: {
    type: Array,
    required: true,
  },
  // Optional shared state from createConfigDraft; tabs pass it when their own
  // setup code also reads or edits the draft.
  state: {
    type: Object,
    default: null,
  },
  // Optional card chrome for the schema fields. Without a title the fields
  // render as a bare grid (for tabs that already wrap the form in a card).
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  // Optional [{ key, title, description, icon, collapsible }]; a field joins the
  // card whose key matches its `section`. Unmatched fields fall into the title
  // card. Collapsible cards start closed (advanced settings). Custom content for a
  // card goes in the `#section-KEY` slot, rendered below that card's fields.
  sections: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();
const slots = useSlots();

const state = props.state || createConfigDraft(props.bot);
const { draft, fieldErrors, generalError, isDirty, setValue, fieldError } =
  state;
const isSaving = ref(false);

const fieldGroups = computed(() => {
  const sectionKeys = props.sections.map(section => section.key);
  const groups = props.sections.map(section => ({
    ...section,
    fields: props.fields.filter(field => field.section === section.key),
  }));
  const rest = props.fields.filter(
    field => !sectionKeys.includes(field.section)
  );
  if (rest.length) {
    groups.unshift({
      key: 'default',
      title: props.title,
      description: props.description,
      icon: props.icon,
      fields: rest,
    });
  }
  // A section may be all custom content (`#section-KEY` slot) with no schema fields.
  return groups.filter(
    group => group.fields.length || slots[`section-${group.key}`]
  );
});

const isFullWidth = field =>
  field.wide || field.type === 'switch' || field.type === 'textarea';

const fieldValue = field => {
  const value = getPath(draft, field.path);
  if (field.type === 'number') return value ?? '';
  if (value !== undefined && value !== null) return value;
  if (field.default !== undefined) return field.default;
  if (field.type === 'switch') return false;
  return '';
};

const resetErrors = () => {
  Object.keys(fieldErrors).forEach(key => delete fieldErrors[key]);
  generalError.value = '';
};

const recordErrors = errors => {
  errors.forEach(({ path, message }) => {
    if (path) {
      fieldErrors[path] = message;
    } else {
      generalError.value = message;
    }
  });
};

const discard = () => {
  resetErrors();
  state.replaceDraft(JSON.parse(state.baseline.value));
};

const save = async () => {
  isSaving.value = true;
  resetErrors();
  try {
    const formData = new FormData();
    formData.append('bot_config', JSON.stringify(draft));
    const { data } = await AgentBotsAPI.update(props.bot.id, formData);
    state.replaceDraft(data.bot_config);
    useAlert(t('AI_AGENT.STUDIO.SAVED'));
    emit('saved', data);
    store.dispatch('agentBots/show', props.bot.id);
  } catch (error) {
    const errors = error?.response?.data?.errors;
    if (Array.isArray(errors) && errors.length) {
      recordErrors(errors);
    } else {
      useAlert(t('AI_AGENT.STUDIO.SAVE_ERROR'));
    }
  } finally {
    isSaving.value = false;
  }
};

// Components rendered inside the form (e.g. IntentSetPicker) edit the same draft via inject.
provideConfigDraft(state);

defineExpose({ draft, save, discard, isDirty, fieldError, setValue });
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="save">
    <p
      v-if="generalError"
      class="px-3 py-2 rounded-lg text-body-main bg-n-ruby-9/10 text-n-ruby-11"
      data-testid="general-error"
    >
      {{ generalError }}
    </p>
    <component
      :is="group.title ? StudioSection : 'div'"
      v-for="group in fieldGroups"
      :key="group.key"
      :title="group.title ? group.title : undefined"
      :description="group.title ? group.description : undefined"
      :icon="group.title ? group.icon : undefined"
      :collapsible="group.title ? !!group.collapsible : undefined"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
        <div
          v-for="field in group.fields"
          :key="field.path"
          class="flex flex-col gap-1.5 min-w-0"
          :class="{ 'md:col-span-2': isFullWidth(field) }"
        >
          <div
            v-if="field.type === 'switch'"
            class="flex items-start justify-between gap-4 px-3 py-2.5 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <div class="flex flex-col gap-0.5 min-w-0">
              <span class="text-label text-n-slate-12">
                {{ t(field.label) }}
              </span>
              <span v-if="field.help" class="text-label-small text-n-slate-10">
                {{ t(field.help) }}
              </span>
            </div>
            <Switch
              class="mt-1"
              :model-value="fieldValue(field)"
              :data-testid="`field-${field.path}`"
              @update:model-value="setValue(field, $event)"
            />
          </div>
          <template v-else>
            <template v-if="field.type === 'select'">
              <span class="text-label text-n-slate-12">
                {{ t(field.label) }}
              </span>
              <Select
                :model-value="fieldValue(field)"
                :options="field.options"
                :error="fieldError(field.path)"
                :data-testid="`field-${field.path}`"
                @update:model-value="setValue(field, $event)"
              />
            </template>
            <TextArea
              v-else-if="field.type === 'textarea'"
              :id="`field-${field.path}`"
              :model-value="fieldValue(field)"
              :label="t(field.label)"
              :placeholder="field.placeholder ? t(field.placeholder) : ''"
              :message="fieldError(field.path)"
              :message-type="fieldError(field.path) ? 'error' : 'info'"
              :data-testid="`field-${field.path}`"
              auto-height
              @update:model-value="setValue(field, $event)"
            />
            <Input
              v-else
              :id="`field-${field.path}`"
              :type="field.type === 'number' ? 'number' : 'text'"
              :model-value="fieldValue(field)"
              :label="t(field.label)"
              :placeholder="field.placeholder ? t(field.placeholder) : ''"
              :message="fieldError(field.path)"
              :message-type="fieldError(field.path) ? 'error' : 'info'"
              :data-testid="`field-${field.path}`"
              @update:model-value="setValue(field, $event)"
            />
            <p v-if="field.help" class="text-label-small text-n-slate-10">
              {{ t(field.help) }}
            </p>
          </template>
        </div>
      </div>
      <slot
        :name="`section-${group.key}`"
        :draft="draft"
        :set-value="setValue"
        :error-for="fieldError"
      />
    </component>
    <slot
      :draft="draft"
      :set-value="setValue"
      :error-for="fieldError"
      :is-dirty="isDirty"
    />
    <div
      class="z-10 transition-transform duration-200 motion-reduce:transition-none"
      :class="isDirty ? 'sticky bottom-4' : ''"
      data-testid="config-form-save-bar"
    >
      <div
        class="flex items-center justify-between gap-3 px-4 py-3 rounded-xl"
        :class="
          isDirty
            ? 'bg-n-solid-3 shadow-lg outline outline-1 -outline-offset-1 outline-n-container'
            : ''
        "
      >
        <span
          v-if="isDirty"
          class="flex items-center gap-2 text-body-main text-n-slate-12"
          data-testid="config-form-dirty"
        >
          <span class="rounded-full size-2 bg-n-amber-9" />
          {{ t('AI_AGENT.STUDIO.SAVE_BAR.UNSAVED') }}
        </span>
        <span v-else />
        <div class="flex items-center gap-2">
          <Button
            v-if="isDirty"
            type="button"
            slate
            faded
            sm
            :label="t('AI_AGENT.STUDIO.SAVE_BAR.DISCARD')"
            :disabled="isSaving"
            data-testid="config-form-discard"
            @click="discard"
          />
          <Button
            type="submit"
            sm
            data-testid="config-form-submit"
            :label="t('AI_AGENT.STUDIO.SAVE')"
            :is-loading="isSaving"
            :disabled="!isDirty"
          />
        </div>
      </div>
    </div>
  </form>
</template>
